import React, { useState, useEffect } from 'react';
import { Award, User, Calendar, FileText, X, Check } from 'lucide-react';
import useCertificationManagementStore from '@/stores/AdminStore/CertificationManagementStore';
import MessageModal from '@/Components/common/MessageModal';
import './IssueCertificate.css';

interface IssueCertificateProps {
  onSuccess?: () => void;
}

// Curated list of certificate titles shown in the dropdown. Kept hard-coded
// because these are configuration-level wording choices, not dynamic data.
const CERTIFICATE_TITLES = [
  'Certificate of Achievement',
  'Certificate of Completion',
  'Certificate of Participation',
  'Certificate of Excellence',
  'Certificate of Recognition',
];

interface FormData {
  certificate_title: string;
  certificate_name: string;
  user_id: string;
  description: string;
  issue_date: string;
  expiry_date: string;
  template_id: number | null;
  notes: string;
}

interface FormErrors {
  certificate_name?: string;
  user_id?: string;
  issue_date?: string;
  expiry_date?: string;
}

const IssueCertificate: React.FC<IssueCertificateProps> = ({ onSuccess }) => {
  const {
    users,
    certificateTemplates,
    certifications,
    loading,
    error,
    fetchUsers,
    fetchCertificateTemplates,
    fetchCertifications,
    issueCertificateToUser,
    clearError,
  } = useCertificationManagementStore();

  const [formData, setFormData] = useState<FormData>({
    certificate_title: 'Certificate of Achievement',
    certificate_name: '',
    user_id: '',
    description: '',
    issue_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    template_id: null,
    notes: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalType, setMessageModalType] = useState<'success' | 'error'>('success');
  const [messageModalTitle, setMessageModalTitle] = useState('');
  const [messageModalMessage, setMessageModalMessage] = useState('');

  useEffect(() => {
    if (users.length === 0) {
      fetchUsers('');
    }
    if (certificateTemplates.length === 0) {
      fetchCertificateTemplates();
    }
    if ((certifications ?? []).length === 0) {
      // Pulls all certifications (active + inactive) so the dropdown isn't
      // restricted by the list page's current status filter.
      fetchCertifications(1, 100, 'all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Set default template
    if (certificateTemplates.length > 0 && !formData.template_id) {
      const defaultTemplate = certificateTemplates.find((t) => t.is_default);
      if (defaultTemplate) {
        setFormData((prev) => ({ ...prev, template_id: defaultTemplate.id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateTemplates]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.certificate_name.trim()) {
      errors.certificate_name = 'Certificate name is required';
    }

    if (!formData.user_id) {
      errors.user_id = 'Please select a user';
    }

    if (!formData.issue_date) {
      errors.issue_date = 'Issue date is required';
    }

    if (
      formData.issue_date &&
      formData.expiry_date &&
      formData.expiry_date < formData.issue_date
    ) {
      errors.expiry_date = 'Expiry date cannot be earlier than issue date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    fetchUsers(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    clearError();

    try {
      const result = await issueCertificateToUser(formData);

      if (result.success) {
        // Reset form
        setFormData({
          certificate_title: 'Certificate of Achievement',
          certificate_name: '',
          user_id: '',
          description: '',
          issue_date: new Date().toISOString().split('T')[0],
          expiry_date: '',
          template_id: certificateTemplates.find((t) => t.is_default)?.id || null,
          notes: '',
        });
        setSearchTerm('');
        setFormErrors({});

        // Show success modal
        setMessageModalType('success');
        setMessageModalTitle('Certificate Issued Successfully!');
        setMessageModalMessage('The certificate has been created and issued to the user.');
        setShowMessageModal(true);
      } else {
        // Show error modal if there's an error message
        setMessageModalType('error');
        setMessageModalTitle('Failed to Issue Certificate');
        setMessageModalMessage(result.message || error || 'An unexpected error occurred. Please try again.');
        setShowMessageModal(true);
      }
    } catch (err: any) {
      console.error('Failed to issue certificate:', err);
      // Show error modal
      setMessageModalType('error');
      setMessageModalTitle('Failed to Issue Certificate');
      setMessageModalMessage(err.message || 'An unexpected error occurred. Please try again.');
      setShowMessageModal(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    // If success, navigate to view certificates tab
    if (messageModalType === 'success' && onSuccess) {
      onSuccess();
    }
    // If error, stay on current tab (do nothing)
  };

  const selectedUser = users.find((u) => u.uuid === formData.user_id);

  return (
    <div className="admin-issue-certificate">
      <form onSubmit={handleSubmit} className="admin-issue-certificate-form">
        <div className="admin-issue-certificate-layout">
          <div className="admin-issue-certificate-main">
            {/* Certificate Information */}
            <div className="admin-issue-certificate-section">
              <div className="admin-issue-certificate-section-header">
                <Award size={20} />
                <h3>Certificate Information</h3>
              </div>

              <div className="admin-issue-certificate-field">
                <label htmlFor="certificate_title">
                  Certificate Title <span className="admin-issue-certificate-required">*</span>
                </label>
                <select
                  id="certificate_title"
                  name="certificate_title"
                  value={formData.certificate_title}
                  onChange={handleInputChange}
                >
                  {CERTIFICATE_TITLES.map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </select>
                <small style={{ color: 'var(--color-secondary-text)', marginTop: '4px', display: 'block' }}>
                  This title will appear prominently on the certificate
                </small>
              </div>

              <div className="admin-issue-certificate-field">
                <label htmlFor="certificate_name">
                  Course/Achievement Name <span className="admin-issue-certificate-required">*</span>
                </label>
                <select
                  id="certificate_name"
                  name="certificate_name"
                  value={formData.certificate_name}
                  onChange={handleInputChange}
                  className={formErrors.certificate_name ? 'error' : ''}
                >
                  <option value="">
                    {(certifications ?? []).length === 0
                      ? 'No certifications available — create one first'
                      : 'Select a certification'}
                  </option>
                  {(certifications ?? []).map((cert) => (
                    <option key={cert.id} value={cert.certification_name}>
                      {cert.certification_name}
                    </option>
                  ))}
                </select>
                {formErrors.certificate_name && (
                  <span className="admin-issue-certificate-error-text">
                    {formErrors.certificate_name}
                  </span>
                )}
                <small style={{ color: 'var(--color-secondary-text)', marginTop: '4px', display: 'block' }}>
                  Pulled from the certifications you've created in the Certification List tab.
                </small>
              </div>

              <div className="admin-issue-certificate-field">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Describe the achievement or purpose of this certificate..."
                />
              </div>

              <div className="admin-issue-certificate-field">
                <label htmlFor="template_id">Certificate Template</label>
                <select
                  id="template_id"
                  name="template_id"
                  value={formData.template_id || ''}
                  onChange={handleInputChange}
                >
                  {certificateTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.template_name} {template.is_default ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Recipient Information */}
            <div className="admin-issue-certificate-section">
              <div className="admin-issue-certificate-section-header">
                <User size={20} />
                <h3>Recipient</h3>
              </div>

              <div className="admin-issue-certificate-field">
                <label htmlFor="user_search">
                  Search User <span className="admin-issue-certificate-required">*</span>
                </label>
                <input
                  type="text"
                  id="user_search"
                  value={searchTerm}
                  onChange={handleUserSearch}
                  placeholder="Search by name or email..."
                  className={formErrors.user_id ? 'error' : ''}
                />
                {formErrors.user_id && (
                  <span className="admin-issue-certificate-error-text">{formErrors.user_id}</span>
                )}
              </div>

              {users.length > 0 && (
                <div className="admin-issue-certificate-user-list">
                  {users.slice(0, 10).map((user) => (
                    <div
                      key={user.uuid}
                      className={`admin-issue-certificate-user-item ${
                        formData.user_id === user.uuid ? 'selected' : ''
                      }`}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, user_id: user.uuid }));
                        setSearchTerm(`${user.first_name} ${user.last_name}`);
                        if (formErrors.user_id) {
                          setFormErrors((prev) => ({ ...prev, user_id: undefined }));
                        }
                      }}
                    >
                      <div className="admin-issue-certificate-user-info">
                        <span className="admin-issue-certificate-user-name">
                          {user.first_name} {user.last_name}
                        </span>
                        <small className="admin-issue-certificate-user-email">{user.email}</small>
                      </div>
                      {formData.user_id === user.uuid && <Check size={16} />}
                    </div>
                  ))}
                </div>
              )}

              {selectedUser && (
                <div className="admin-issue-certificate-selected-user">
                  <strong>Selected:</strong> {selectedUser.first_name} {selectedUser.last_name} (
                  {selectedUser.email})
                </div>
              )}
            </div>

            {/* Dates */}
            <div className="admin-issue-certificate-section">
              <div className="admin-issue-certificate-section-header">
                <Calendar size={20} />
                <h3>Dates</h3>
              </div>

              <div className="admin-issue-certificate-field-row">
                <div className="admin-issue-certificate-field">
                  <label htmlFor="issue_date">
                    Issue Date <span className="admin-issue-certificate-required">*</span>
                  </label>
                  <input
                    type="date"
                    id="issue_date"
                    name="issue_date"
                    value={formData.issue_date}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        issue_date: next,
                        expiry_date:
                          next && prev.expiry_date && prev.expiry_date < next
                            ? ''
                            : prev.expiry_date,
                      }));
                      if (formErrors.issue_date) {
                        setFormErrors((prev) => ({ ...prev, issue_date: undefined }));
                      }
                      if (formErrors.expiry_date) {
                        setFormErrors((prev) => ({ ...prev, expiry_date: undefined }));
                      }
                    }}
                    className={formErrors.issue_date ? 'error' : ''}
                  />
                  {formErrors.issue_date && (
                    <span className="admin-issue-certificate-error-text">
                      {formErrors.issue_date}
                    </span>
                  )}
                </div>

                <div className="admin-issue-certificate-field">
                  <label htmlFor="expiry_date">Expiry Date (Optional)</label>
                  <input
                    type="date"
                    id="expiry_date"
                    name="expiry_date"
                    value={formData.expiry_date}
                    min={formData.issue_date || undefined}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({ ...prev, expiry_date: value }));
                      if (formErrors.expiry_date) {
                        setFormErrors((prev) => ({ ...prev, expiry_date: undefined }));
                      }
                    }}
                    className={formErrors.expiry_date ? 'error' : ''}
                  />
                  {formErrors.expiry_date && (
                    <span className="admin-issue-certificate-error-text">
                      {formErrors.expiry_date}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="admin-issue-certificate-section">
              <div className="admin-issue-certificate-section-header">
                <FileText size={20} />
                <h3>Internal Notes</h3>
              </div>

              <div className="admin-issue-certificate-field">
                <label htmlFor="notes">Notes (Internal Use Only)</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={2}
                  placeholder="Add any internal notes about this certificate..."
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="admin-issue-certificate-sidebar">
            <div className="admin-issue-certificate-sidebar-section">
              <h4>Actions</h4>
              <button
                type="submit"
                className="admin-issue-certificate-submit-btn"
                disabled={submitting || loading}
              >
                {submitting ? (
                  <>
                    <div className="spinner-small"></div>
                    <span>Issuing...</span>
                  </>
                ) : (
                  <>
                    <Award size={16} />
                    <span>Issue Certificate</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className="admin-issue-certificate-clear-btn"
                onClick={() => {
                  setFormData({
                    certificate_title: 'Certificate of Achievement',
                    certificate_name: '',
                    user_id: '',
                    description: '',
                    issue_date: new Date().toISOString().split('T')[0],
                    expiry_date: '',
                    template_id: certificateTemplates.find((t) => t.is_default)?.id || null,
                    notes: '',
                  });
                  setSearchTerm('');
                  setFormErrors({});
                  clearError();
                }}
                disabled={submitting}
              >
                <X size={16} />
                <span>Clear Form</span>
              </button>
            </div>

            <div className="admin-issue-certificate-sidebar-section admin-issue-certificate-tips">
              <h4>💡 Tips</h4>
              <ul>
                <li>Use descriptive certificate names</li>
                <li>Add a clear description of the achievement</li>
                <li>Double-check the recipient before issuing</li>
                <li>Set expiry dates for time-limited certifications</li>
              </ul>
            </div>
          </div>
        </div>
      </form>

      {/* Success/Error Modal */}
      <MessageModal
        isOpen={showMessageModal}
        onClose={handleCloseMessageModal}
        type={messageModalType}
        title={messageModalTitle}
        message={messageModalMessage}
      />
    </div>
  );
};

export default IssueCertificate;
